import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useTranslate } from 'src/locales';
import { useGetMyCompaniesQuery } from 'src/services/connections/connections.service';
import { PresentationTemplateSummary } from 'src/services/presentations/presentations.types';
import { useUpdatePresentationTemplateMutation } from 'src/services/presentations/presentations.service';

type Company = { id: string; name: string };

interface Props {
  template: PresentationTemplateSummary;
  onClose: VoidFunction;
}

/**
 * Which client an agency template is for (JT1-1887, C12). A presentation for one of that client's
 * jobs starts on it; clearing the client makes it an ordinary template again.
 */
export default function TemplateClientDialog({ template, onClose }: Props) {
  const { t } = useTranslate();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Company | null>(template.client_company ?? null);

  const { currentData: companies, isFetching } = useGetMyCompaniesQuery({ params: { page: 1, per_page: 10, search_query: search } });
  const [update, { isLoading }] = useUpdatePresentationTemplateMutation();

  const onSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), []);
  const options: Company[] = (companies?.results ?? []).map((company) => ({ id: company.id, name: company.company_name }));

  const save = async (company: Company | null) => {
    try {
      await update({ id: template.key, client_company_id: company?.id ?? null }).unwrap();
      enqueueSnackbar(t(company ? 'presentations.templates.client_set' : 'presentations.templates.client_cleared', { name: company?.name ?? '' }), { variant: 'success' });
      onClose();
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('presentations.templates.client_title', { name: template.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('presentations.templates.client_body')}</Typography>
          <Autocomplete
            options={options}
            value={selected}
            loading={isFetching}
            filterOptions={(items) => items}
            getOptionLabel={(company) => company.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setSelected(value)}
            onInputChange={(_event, value, reason) => reason === 'input' && onSearch(value)}
            renderInput={(params) => <TextField {...params} label={t('presentations.templates.client_label')} />}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {template.client_company && (
          <Button color="inherit" disabled={isLoading} onClick={() => save(null)} sx={{ mr: 'auto' }}>
            {t('presentations.templates.client_clear')}
          </Button>
        )}
        <Button variant="outlined" color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!selected || selected.id === template.client_company?.id} onClick={() => save(selected)}>
          {t('common.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
